import { publicAxiosInstance } from "$root/api/config";
import env from "$root/env";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { accessToken$, mfaEnrollmentToken$ } from "../session/atom";
import { type IUser, type MfaStatus, MfaStatus as MfaStatusEnum } from ".";

/**
 * Forced TOTP enrollment (workaround for OPTIONAL-mode Cognito, which never
 * issues an `MFA_SETUP` challenge on its own — D0006).
 *
 * Both calls run **post-login**, against endpoints we own. A gated user's token is
 * held in `mfaEnrollmentToken$` (not `accessToken$`), so these go through
 * `publicAxiosInstance` with an explicit `Authorization` header: that bypasses the
 * auth interceptors, whose 401 handler would otherwise fire the `unauthorized` →
 * silent-renew → logout cascade (and write `accessToken$`) mid-enrollment. Nothing
 * Cognito-shaped crosses the wire: the client only ever sees an open-standard
 * `otpauth://` URI and a 6-digit code. These two endpoints are not (yet) part of the
 * generated OpenAPI client, so they are wired by hand here; when the backend ships
 * them into the spec, swap the bodies for `getApiGen(...)` calls.
 */

/**
 * Bearer header for the enrollment calls, from the held token (fall back to the
 * live one for the non-gated edge case). Throws if there is no token at all.
 */
function enrollmentAuthConfig(): { headers: { Authorization: string } } {
	const token = readAtom(mfaEnrollmentToken$) ?? readAtom(accessToken$);
	if (!token) {
		throw new Error("No token available for MFA enrollment");
	}
	return { headers: { Authorization: `Bearer ${token}` } };
}

/** Backend endpoints, relative to `env.apiOrigin`. */
const MFA_ENROLL_START_PATH = "/mfa/enroll/start";
const MFA_ENROLL_VERIFY_PATH = "/mfa/enroll/verify";

export type MfaEnrollStartResponse = {
	/** `otpauth://` URI to render as a QR code (open standard, vendor-neutral). */
	otpauthUri: string;
	/** Base32 TOTP secret, offered for manual entry when a QR scan isn't possible. */
	secret?: string;
};

export const mfaRepository = {
	/**
	 * Begin enrollment: the server calls `AssociateSoftwareToken` and returns the
	 * `otpauth://` URI (and secret) for the user to add to their authenticator app.
	 */
	enrollStart: async (): Promise<MfaEnrollStartResponse> => {
		const { data } = await publicAxiosInstance.post<Partial<MfaEnrollStartResponse> & { otpConfigString?: string }>(
			`${env.apiOrigin}${MFA_ENROLL_START_PATH}`,
			undefined,
			enrollmentAuthConfig(),
		);
		// Tolerate small naming variations until the contract is finalized.
		const otpauthUri = data.otpauthUri ?? data.otpConfigString;
		if (!otpauthUri) {
			throw new Error("MFA enrollment start returned no otpauth URI");
		}
		return { otpauthUri, secret: data.secret };
	},

	/**
	 * Confirm enrollment with a code from the authenticator app: the server calls
	 * `VerifySoftwareToken` then `AdminSetUserMFAPreference` (TOTP enabled +
	 * preferred). On success `mfaStatus` resolves `ENROLLED` and the gate clears.
	 */
	enrollVerify: async (code: string): Promise<void> => {
		await publicAxiosInstance.post(`${env.apiOrigin}${MFA_ENROLL_VERIFY_PATH}`, { code }, enrollmentAuthConfig());
	},
} as const;

/** Whether the backend has flagged this MFA status as requiring enrollment. */
export function isMustEnroll(mfaStatus: MfaStatus | undefined): boolean {
	return mfaStatus === MfaStatusEnum.MustEnroll;
}

/**
 * The enrollment gate: a signed-in user the backend has flagged `MUST_ENROLL` is
 * forced through TOTP enrollment. The backend owns the "who must enroll" decision
 * (role + no configured device), so the frontend just reads the resolved status.
 * Any other value — including a missing status (`undefined`) — leaves the gate
 * closed, so we never lock users out against a backend that doesn't yet report it.
 *
 * Never gates while impersonating: enrollment is about the real signed-in user's own
 * device, not the impersonated identity's MFA status (`authSessionInit` mirrors this).
 */
export function needsMfaEnrollment(user: IUser): boolean {
	return user.signedIn && !user.impersonating && isMustEnroll(user.mfaStatus);
}
